/* AlertCategoriesRow — segment of segmented toggles, one per alert tier
 * (Storm watch · Rain inbound · Lightning · Heat · Cold · Air quality).
 *
 * Reads + writes /api/alert-categories. Re-fetches whenever the signed-in
 * user changes so an in-session Google sign-in flips us from the anon
 * placeholder to the live toggle list without needing a page reload.
 */
import { useEffect, useState, useRef } from "react";
import {
  CloudRain,
  Drop,
  Lightning,
  Sun,
  Snowflake,
  Cloud,
} from "@phosphor-icons/react";
import { getAlertCategories, putAlertCategories } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const CATEGORIES = [
  {
    key: "storm",
    icon: CloudRain,
    label: "Storm watch",
    sub: "Heavy rain about to load",
    tone: "#0369a1",
  },
  {
    key: "rain_inbound",
    icon: Drop,
    label: "Rain in 30–60 min",
    sub: "Grab an umbrella",
    tone: "#0891b2",
  },
  {
    key: "lightning",
    icon: Lightning,
    label: "Lightning nearby",
    sub: "Strikes within 15 km",
    tone: "#b45309",
  },
  {
    key: "heat",
    icon: Sun,
    label: "Hot day · hydrate",
    sub: "Morning briefing · ≥38°C",
    tone: "#dc2626",
  },
  {
    key: "cold",
    icon: Snowflake,
    label: "Cold morning · layer up",
    sub: "Morning briefing · ≤5°C",
    tone: "#2563eb",
  },
  {
    key: "air",
    icon: Cloud,
    label: "Air quality alert",
    sub: "AQI ≥150",
    tone: "#6b7280",
  },
];

export default function AlertCategoriesRow({ theme }) {
  const { user } = useAuth();
  const [cats, setCats] = useState(null);
  const [signedIn, setSignedIn] = useState(true);
  const [loading, setLoading] = useState(true);
  const saveTimer = useRef(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    // Only treat a 401 as "anon" — any other failure (network blip, transient
    // 5xx, stale SW cache) keeps the toggle list visible and lets the next
    // PUT recover. Previously every error flipped to the sign-in hint, which
    // a user reported seeing even after logging in successfully.
    getAlertCategories()
      .then((r) => {
        if (!alive) return;
        setCats(r?.categories || null);
        setSignedIn(true);
        setLoading(false);
      })
      .catch((err) => {
        if (!alive) return;
        const status = err?.response?.status;
        if (status === 401) {
          setSignedIn(false);
          setCats(null);
        } else {
          // Network / transient — keep the previous view, retry will happen
          // next time Settings is opened or the user toggles a category.
          setSignedIn(true);
        }
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [user?.user_id]);  // re-fetch whenever the signed-in user changes

  const toggle = (key) => {
    if (!cats) return;
    const next = { ...cats, [key]: !cats[key] };
    setCats(next);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      putAlertCategories({ [key]: next[key] }).catch(() => {
        /* silent — UI already optimistic */
      });
    }, 350);
  };

  const dark = theme.id === "night" || theme.id === "storm";
  const rowBg = dark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)";

  if (!signedIn) {
    return (
      <div
        className="text-[11.5px] px-2 py-2 rounded-xl"
        style={{
          background: rowBg,
          color: theme.textMuted,
          border: `1px dashed ${theme.surfaceBorder}`,
        }}
        data-testid="alert-categories-anon"
      >
        Sign in with Google to choose which alerts you want.
      </div>
    );
  }

  if (loading || !cats) {
    return (
      <div className="text-[11.5px] px-2 py-2" style={{ color: theme.textMuted }}>
        Loading…
      </div>
    );
  }

  return (
    <div
      className="rounded-2xl divide-y"
      style={{
        background: rowBg,
        border: `1px solid ${theme.surfaceBorder}`,
        borderColor: theme.surfaceBorder,
      }}
      data-testid="alert-categories-list"
    >
      {CATEGORIES.map((cat, idx) => {
        const Icon = cat.icon;
        const on = !!cats[cat.key];
        return (
          <button
            key={cat.key}
            type="button"
            onClick={() => toggle(cat.key)}
            data-testid={`alert-cat-${cat.key}`}
            className="w-full flex items-center gap-3 px-3 py-2.5 transition-colors"
            style={{
              borderTop: idx === 0 ? "none" : `1px solid ${theme.surfaceBorder}`,
            }}
          >
            <span
              className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center"
              style={{
                background: on
                  ? `${cat.tone}22`
                  : dark
                    ? "rgba(255,255,255,0.06)"
                    : "rgba(15,23,42,0.06)",
                color: on ? cat.tone : theme.textMuted,
              }}
            >
              <Icon size={14} weight={on ? "fill" : "bold"} />
            </span>
            <div className="flex-1 min-w-0 text-left">
              <div
                className="text-[12.5px] font-semibold leading-tight"
                style={{ color: theme.textPrimary }}
              >
                {cat.label}
              </div>
              <div
                className="text-[10px] leading-snug mt-0.5"
                style={{ color: theme.textMuted }}
              >
                {cat.sub}
              </div>
            </div>
            <span
              className="shrink-0 text-[10px] font-bold uppercase tracking-[0.14em]"
              style={{ color: on ? cat.tone : theme.textMuted }}
            >
              {on ? "On" : "Off"}
            </span>
          </button>
        );
      })}
    </div>
  );
}
